import { useState } from 'react';

import { type CheckboxesListOption, CheckboxList } from './checkbox-list';
import { CheckboxListWithOther } from './checkbox-list-with-other';
import { OptionsVisibilityToggle } from './options-visibility-toggle';

type Props<TOption extends CheckboxesListOption> = {
    options: TOption[];
    values: TOption['value'][];
    onValuesChange: (values: TOption['value'][]) => void;
    columns?: 1 | 2;
    otherValue?: string;
    onOtherValueChange?: (value: string | undefined) => void;
    defaultShowOnlySelected?: boolean;
};

export function ToggleableViewCheckboxList<TOption extends CheckboxesListOption>({
    options,
    values,
    onValuesChange,
    columns = 2,
    otherValue,
    onOtherValueChange,
    defaultShowOnlySelected,
}: Props<TOption>) {
    // Compute the initial default once — based on whether there's any data on first render.
    // If the caller passed a value, honor it. Otherwise: data → "show only selected", empty → "show all".
    const [initialDefault] = useState(
        () => defaultShowOnlySelected ?? (values.length > 0 || (otherValue ?? '').length > 0),
    );

    return (
        <OptionsVisibilityToggle defaultShowOnlySelected={initialDefault}>
            {({ showOnlySelected }) =>
                onOtherValueChange ? (
                    <CheckboxListWithOther
                        options={options}
                        values={values}
                        onValuesChange={onValuesChange as (values: string[]) => void}
                        columns={showOnlySelected ? 1 : columns}
                        otherValue={otherValue}
                        onOtherValueChange={onOtherValueChange}
                        showOnlySelected={showOnlySelected}
                    />
                ) : (
                    <CheckboxList
                        options={options}
                        values={values}
                        onValuesChange={onValuesChange as (values: string[]) => void}
                        columns={showOnlySelected ? 1 : columns}
                        showOnlySelected={showOnlySelected}
                    />
                )
            }
        </OptionsVisibilityToggle>
    );
}
